/**
 * The unified interaction-resolve path — one entry for every HITL answer
 * (question, approval, permission, hands consent, connector auth).
 *
 * Resolution is addressed by the interaction id we already hold, NOT by which
 * task happens to be open, so the same code serves an in-task Submit, a board
 * CTA on a non-selected task, and a cross-tab resolve. Phase 9: everything
 * goes over POST /api/interactions/:id/resolve — the legacy socket emit paths
 * (QUESTION_ANSWER, APPROVAL_RESPONSE, …) are gone, which is also why a
 * disconnected socket must not stop it.
 *
 * Lives outside taskStore.ts so the store body stays a list of thin actions;
 * the store reach comes in as `{ get, set }` rather than an import, which
 * would close a module cycle.
 */

import { captureV1 } from "@/lib/analytics-v1";
import { ANALYTICS_EVENTS } from "@bash/shared";
import { resolveInteractionApi } from "@/lib/api/interactions";
import { buildInteractionOutcome } from "../interactionOutcome";
import {
  settleMainPending,
  restoreMainPending,
  findMainPending,
  pendingKey,
} from "../pendingQueue";
import type {
  ContentBlock,
  PendingInteraction,
  QuestionBlock,
  TaskState,
  ToolUseBlock,
} from "../types";
import type {
  InteractionResponse,
  InteractionTarget,
  TaskStoreSeam,
} from "../taskStoreTypes";

export function respondToInteraction(
  seam: TaskStoreSeam,
  response: InteractionResponse,
  target?: InteractionTarget
): void {
  const { get, set } = seam;
  const { selectedTaskId, tasks } = get();
  // Resolution is addressed by the interaction id we already hold, not by
  // which task happens to be open. Prefer an explicit target task (board CTA
  // on a non-selected/cross-tab task), fall back to the selected task so the
  // existing in-task Submit/Skip/approval flows stay byte-identical.
  const resolveTaskId = target?.taskId || selectedTaskId;
  const task = resolveTaskId ? tasks.get(resolveTaskId) : null;
  // T9 — a subagent-lane resolve addresses its lane in `pendingByLane`; the
  // main agent (no `subagentId`) keeps reading `pendingInteraction`. Everything
  // downstream uses `pi` uniformly, so a main-agent resolve is byte-identical.
  const laneId = target?.subagentId;
  const pi = findPending(task, laneId, target?.interactionId);

  if (!pi) {
    resolveByIdOnly(seam, response, target, resolveTaskId, laneId);
    return;
  }

  // Resolve is an HTTP POST, NOT a socket emit — so it must fire even when the
  // frontend socket is disconnected. (The old `if (!socket?.connected) return`
  // bail made Allow/Deny silently inert on a dropped socket, eating the answer.)

  // Phase 9: all interactions resolve through POST /api/interactions/:id/resolve.
  // An explicit interactionId (from the board CTA) addresses the exact row;
  // otherwise fall back to the live interaction's own id (in-task flows).
  const outcome = buildInteractionOutcome(response, pi);
  // A file answer's files ride the POST beside the outcome (spec 2026-09-24);
  // with none, the body is today's.
  resolveInteractionApi(
    target?.interactionId ?? pi.interactionId ?? pi.requestId,
    outcome,
    response.files
  ).then(
    // Analytics after the server took the answer: a failed resolve is no
    // resolution (the ask comes back), and its retry is the one that counts.
    () => captureInteractionAnalytics(response, resolveTaskId, pi),
    (err) => {
      console.warn(
        `[_respondToInteraction] resolveInteractionApi failed: ${err}`
      );
      // Engines T5: a plan_limit tap onto credits the person does not have is
      // refused BEFORE the card settles (409 no_credits), so the card is still
      // answerable. It comes back saying so under its Vestra row; a toast on
      // top would say it twice.
      const noCredits =
        pi.type === "plan_limit" && errorCodeOf(err) === "no_credits";
      // The resolve POST failed — don't silently eat the user's answer. Restore
      // the pending picker so they can retry, and surface a transient error.
      get()._mutateTasks((tasks) => {
        const t = tasks.get(resolveTaskId!);
        if (t)
          restorePending(
            t,
            noCredits ? { ...pi, refused: "no_credits" } : pi,
            laneId
          );
      });
      if (noCredits) return;
      set((state) => {
        state.interactionError = resolveFailedMessage(err);
      });
    }
  );

  // Clear pending interaction + update UI blocks
  get()._mutateTasks((tasks) => {
    const t = tasks.get(resolveTaskId!);
    if (t) settleResolved(t, response, pi, laneId);
  });
}

/**
 * The toast for a failed resolve. A file answer the box took none of (the
 * server's 409 files_not_synced, spec 2026-09-24) says so and offers the link
 * path; every other failure keeps the one line. The code is a field on the
 * API's error, read without importing its class (tests mock that module).
 */
function resolveFailedMessage(err: unknown): string {
  return errorCodeOf(err) === "files_not_synced"
    ? "Your file couldn't reach the agent. Try again or paste a link."
    : "Couldn't send your response. Tap to try again.";
}

/** The server's own code on a refused resolve (ResolveInteractionError.code). */
function errorCodeOf(err: unknown): unknown {
  return typeof err === "object" && err !== null
    ? (err as { code?: unknown }).code
    : undefined;
}

/**
 * The interaction this resolve addresses. A subagent lane is keyed directly;
 * on the main lane an explicit interactionId (board CTA / cross-tab) may
 * address a QUEUED sibling ask rather than the visible head — look it up
 * across head + queue, falling back to the head (in-task flows, legacy callers).
 */
function findPending(
  task: TaskState | null | undefined,
  laneId: string | undefined,
  interactionId: string | undefined
): PendingInteraction | null {
  if (laneId) return task?.pendingByLane?.[laneId] ?? null;
  return (
    (interactionId && task ? findMainPending(task, interactionId) : null) ??
    task?.pendingInteraction ??
    null
  );
}

/**
 * Resolve-by-id branch: a non-selected/cross-tab task has no live
 * pendingInteraction, but the caller handed us its interaction id directly.
 * resolveInteractionApi addresses the row by id — no live store needed, and
 * no socket required (it's an HTTP resolve, not a socket emit). Only auth
 * resolutions reach this path today (resolveConnection/declineConnection).
 */
function resolveByIdOnly(
  seam: TaskStoreSeam,
  response: InteractionResponse,
  target: InteractionTarget | undefined,
  resolveTaskId: string | null | undefined,
  laneId: string | undefined
): void {
  if (!target?.interactionId) return;
  const outcome =
    response.approved === false
      ? { behavior: "deny" as const, reason: "user_declined" as const }
      : { behavior: "allow" as const };
  resolveInteractionApi(target.interactionId, outcome).catch((err) => {
    console.warn(
      `[_respondToInteraction] resolveInteractionApi (by id) failed: ${err}`
    );
  });
  // Clear the live pending if this task happens to be in the Map (so the card
  // disappears immediately even before the lifecycle flip lands).
  if (!resolveTaskId) return;
  seam.get()._mutateTasks((tasks) => {
    const t = tasks.get(resolveTaskId);
    if (!t) return;
    if (laneId) delete t.pendingByLane?.[laneId];
    else settleMainPending(t, target?.interactionId);
  });
}

/**
 * Put a failed resolve's ask back so the user can retry.
 *
 * Guard: if a NEWER pending arrived for the same lane while the POST was in
 * flight (different requestId), leave it — never clobber a fresher prompt. On
 * the main lane the failed ask goes back at the head; a sibling promoted by
 * the optimistic settle steps back behind it (nothing is clobbered — the queue
 * holds both).
 */
function restorePending(
  t: TaskState,
  pi: PendingInteraction,
  laneId: string | undefined
): void {
  if (laneId) {
    const current = t.pendingByLane?.[laneId];
    if (!current || current.requestId === pi.requestId) {
      (t.pendingByLane ??= {})[laneId] = pi;
      if (t.streamingMessage) t.phase = "waiting";
    }
    return;
  }
  restoreMainPending(t, pi);
  t.phase = "idle";
}

/**
 * interaction:resolved (analytics v1) for the two asks the task view answers
 * in place. It replaced question:answered and permission:responded.
 */
function captureInteractionAnalytics(
  response: InteractionResponse,
  resolveTaskId: string | null | undefined,
  pi: PendingInteraction
): void {
  const ids = {
    ...(resolveTaskId ? { task_id: resolveTaskId } : {}),
    ...(pi.interactionId ? { interaction_id: pi.interactionId } : {}),
  };
  if (response.type === "question") {
    captureV1(ANALYTICS_EVENTS.INTERACTION_RESOLVED, {
      interaction_kind: "question",
      resolution: "answered",
      ...ids,
    });
  } else if (response.type === "permission") {
    captureV1(ANALYTICS_EVENTS.INTERACTION_RESOLVED, {
      interaction_kind: "permission",
      resolution: response.approved ? "approved" : "denied",
      ...ids,
    });
  }
}

/** Retract the resolved ask and reflect the answer on the rendered blocks. */
function settleResolved(
  t: TaskState,
  response: InteractionResponse,
  pi: PendingInteraction,
  laneId: string | undefined
): void {
  // Clear only the resolved interaction. The main (`''`) lane lives in
  // `pendingInteraction` + `pendingQueue` (settling the head promotes the
  // next queued ask); subagent lanes live in `pendingByLane[subagentId]`.
  if (laneId) delete t.pendingByLane?.[laneId];
  else settleMainPending(t, pendingKey(pi));

  // Engines T5: Wait is not a resume. No turn opens until the plan resets
  // (hours, maybe), so the task rests where it is: never "picking up where it
  // left off", never a working phase. The waiting line says when it picks up.
  if (pi.type === "plan_limit" && response.choice === "wait") {
    if (!laneId) t.resumingFrom = null;
    return;
  }

  if (response.type !== "auth") {
    // T9 resolve-scoping: keep the turn in 'waiting' while ANY lane (main or
    // a sibling subagent) still has a pending interaction; only resume
    // 'processing' once every lane has cleared. A main-agent resolve with no
    // subagent lanes ⇒ 'processing' (byte-identical to today).
    const anyPending =
      t.pendingInteraction != null ||
      (t.pendingByLane && Object.keys(t.pendingByLane).length > 0);
    if (t.streamingMessage) {
      t.phase = anyPending ? "waiting" : "processing";
    } else if (!laneId && !anyPending) {
      // The ask ENDED the turn (AGENT_END paused:true → 'idle'; its overlay
      // long retired by the sync). The answer is a send: the runner resumes on
      // it, and until that turn opens the task is 'waiting' — sent, turn not
      // yet open, the state an existing-task send puts it in. Left 'idle', the
      // story read the still-running ask step as "stuck" and the resume gap
      // as "done" (founder 09-01: the line "vibrates"). SESSION_STATE_CHANGED
      // {running} carries it to 'processing'; a lost resume drops back to
      // 'idle' on the watchdog's sync, as any send would.
      t.phase = "waiting";
      t.lastActivityAt = new Date();
    }
  }

  if (response.type === "question") {
    // A streaming turn owns the live blocks; once it has sealed, the answer
    // has to land on the committed thread instead.
    if (t.streamingMessage) {
      markQuestionAnswered(t.streamingMessage.content, pi, response.answers);
    } else {
      for (const m of t.messages)
        markQuestionAnswered(m.content, pi, response.answers);
    }
  }

  if (response.type === "permission" && t.streamingMessage) {
    for (const block of t.streamingMessage.content) {
      if (
        block.type === "tool_use" &&
        (block as ToolUseBlock).toolUseId === pi.toolUseId
      ) {
        (block as ToolUseBlock).permissionPending = false;
      }
    }
  }
}

function markQuestionAnswered(
  content: ContentBlock[],
  pi: PendingInteraction,
  answers: Record<string, string> | undefined
): void {
  for (let i = 0; i < content.length; i++) {
    const block = content[i];
    if (
      block.type === "question" &&
      (block as QuestionBlock).toolUseId === pi.toolUseId
    ) {
      (block as QuestionBlock).answered = true;
      (block as QuestionBlock).answers = answers;
    }
    if (
      block.type === "tool_use" &&
      (block as ToolUseBlock).toolName === "AskUserQuestion" &&
      (block as ToolUseBlock).status === "running"
    ) {
      (block as ToolUseBlock).userAnswers = answers;
    }
  }
}
